/**
 * Route prefetching utilities for performance optimization
 */

const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route component using dynamic import
 */
export const prefetchRoute = async (route: string): Promise<void> => {
  if (prefetchedRoutes.has(route)) {
    return; // Already prefetched
  }

  try {
    switch (route) {
      case '/dashboards':
        await import('@/pages/Dashboards');
        break;
      case '/data-models':
        await import('@/pages/DataModels');
        break;
      case '/data-subscriptions':
        await import('@/pages/DataSubscriptions');
        break;
      case '/chat':
        await import('@/pages/Chat');
        break;
      // Light pages don't need prefetching as they're eagerly loaded
      default:
        return;
    }
    
    prefetchedRoutes.add(route);
  } catch (error) {
    console.warn(`Failed to prefetch route ${route}:`, error);
  }
};

/**
 * Prefetch multiple routes with idle callback
 */
export const prefetchRoutesOnIdle = (routes: string[]): void => {
  const prefetchNext = (index = 0) => {
    if (index >= routes.length) return;
    
    const scheduleNext = () => {
      // Use requestIdleCallback if available, otherwise setTimeout
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => prefetchNext(index + 1));
      } else {
        setTimeout(() => prefetchNext(index + 1), 100);
      }
    };

    prefetchRoute(routes[index])
      .then(scheduleNext)
      .catch(scheduleNext);
  };

  prefetchNext();
};